import * as Yup from 'yup';

const INITIAL_REPORT_TEXT_VALUES = {
  type: '',
  level: '',
  sequence: '',
};

export const buildInitialValues = (config) => {
  return {
    section: config.availableSections.length > 0 ? config.availableSections[0].code : '',
    dimension: {
      code: '',
      description: '',
      firstLowAdjective: '',
      secondLowAdjective: '',
      firstHighAdjective: '',
      secondHighAdjective: '',
      isDistortion: false,
    },
    context: {
      type: '',
      code: '',
      content: '',
    },
    items: [],
    reportTexts: [],
  };
};

export const buildValidationSchema = () => {
  return Yup.object().shape({
    section: Yup.string().required('Section is required'),
  });
};

export const getNextReportTextValues = (values, config) => {
  const { type, level, sequence } = values;

  if (type === 'TEAM_REPORT')
    return {
      type,
      level: '',
      sequence: '',
    };

  const filteredTextLevels = config.availableTextLevels.find((textLevel) => textLevel.reportTextType === type).levels;

  const filteredSequences = config.availableSequences.find(
    (reportTextSequence) => reportTextSequence.reportTextType === type && reportTextSequence.level === level,
  ).sequences;

  const isLastSequence = filteredSequences.indexOf(sequence) === filteredSequences.length - 1;
  const isLastLevel = filteredTextLevels.indexOf(level) === filteredTextLevels.length - 1;
  const isLastType = config.availableReportTextTypes.indexOf(type) === config.availableReportTextTypes.length - 1;

  const allTextLevelSequenceCombinationsWereEntered = isLastType && isLastLevel && isLastSequence;
  const nextSequenceShouldBePreselected =
    (!isLastType && !isLastLevel && !isLastSequence) ||
    (!isLastType && isLastLevel && !isLastSequence) ||
    (isLastType && !isLastLevel && !isLastSequence) ||
    (isLastType && isLastLevel && !isLastSequence);
  const nextLevelAndSequenceShouldBePreselected =
    (!isLastType && !isLastLevel && isLastSequence) || (isLastType && !isLastLevel && isLastSequence);
  const nextTypeLevelAndSequenceShouldBePreselected = !isLastType && isLastLevel && isLastSequence;

  if (allTextLevelSequenceCombinationsWereEntered) {
    return INITIAL_REPORT_TEXT_VALUES;
  }

  if (nextSequenceShouldBePreselected) {
    const nextSequence = filteredSequences[filteredSequences.indexOf(sequence) + 1];

    return {
      type,
      level,
      sequence: nextSequence,
    };
  }

  if (nextLevelAndSequenceShouldBePreselected) {
    const nextLevel = filteredTextLevels[filteredTextLevels.indexOf(level) + 1];
    const nextSequence = config.availableSequences.find(
      (reportTextSequence) => reportTextSequence.reportTextType === type && reportTextSequence.level === nextLevel,
    ).sequences[0];

    return {
      type,
      level: nextLevel,
      sequence: nextSequence,
    };
  }

  if (nextTypeLevelAndSequenceShouldBePreselected) {
    const nextType = config.availableReportTextTypes[config.availableReportTextTypes.indexOf(type) + 1];
    const nextLevel = config.availableTextLevels.find((textLevel) => textLevel.reportTextType === nextType).levels[0];
    const nextSequence = config.availableSequences.find(
      (reportTextSequence) => reportTextSequence.reportTextType === nextType && reportTextSequence.level === nextLevel,
    ).sequences[0];

    return {
      type: nextType,
      level: nextLevel,
      sequence: nextSequence,
    };
  }

  return INITIAL_REPORT_TEXT_VALUES;
};

export const getBehaviorDimensionInput = (values) => ({
  code: values.dimension.code,
  displayName: values.dimension.displayName,
  description: values.dimension.description,
  lowAdjectives: [values.dimension.firstLowAdjective, values.dimension.secondLowAdjective],
  highAdjectives: [values.dimension.firstHighAdjective, values.dimension.secondHighAdjective],
  isDistortion: values.dimension.isDistortion,
});

export const getCognitiveDimensionInput = (values) => ({
  code: values.dimension.code,
  displayName: values.dimension.displayName,
  description: values.dimension.description,
});

export const getBehaviorItemsInput = (values) =>
  values.items.map((item) => ({
    code: item.code,
    text: item.text,
    itemType: item.type,
    valenceType: item.valence,
    itemAnswerTypeId: item.answerType,
    contextId: item.contextId,
  }));

export const getCognitiveItemsInput = (values) =>
  values.items.map((item) => ({
    code: item.code,
    text: item.text,
    itemType: item.type,
    itemAnswerTypeId: item.answerType,
    itemAnswers: item.answers.map((answer) => ({
      text: answer.text,
      isCorrect: !!answer.isCorrect,
    })),
    contextCode: item.contextCode,
  }));

export const getReportTextsInput = (values) =>
  values.reportTexts.map((reportText) => ({
    code: reportText.code,
    text: reportText.text,
    reportTextType: reportText.type,
    textLevel: reportText.level,
    sequence: reportText.sequence,
  }));

export const INITIAL_CONFIG = {
  availableContextTypes: [],
  availableContexts: [],
  availableItemAnswerTypes: [],
  availableItemTypes: [],
  availableReportTextTypes: [],
  availableSections: [],
  availableSequences: [],
  availableTextLevels: [],
  availableValenceTypes: [],
  reportTextValues: INITIAL_REPORT_TEXT_VALUES,
};
